import { useEffect, useState } from "react";
import {
  unitToSeconds,
  secondsToUnitValue,
  parseTimeStringToSeconds,
  formatSecondsToTimeString,
} from "../../utils/timeDurationUtils";

interface TimeDurationInputProps {
  id?: string;
  measurement: number | null;
  unit?: string;
  onChange: (measurement: number | null, unit?: string) => void;
  onCommit?: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

/**
 * Single free-text time entry for athletics-style values. Reads its own
 * shape from what's typed - "30.30", "1:08.90", "2:30:32" - and normalizes
 * on commit (e.g. "1:75" -> "2:15"). Converts back into whatever unit the
 * field already stores, so the saved measurement/unit shape never changes.
 */
export default function TimeDurationInput({
  id,
  measurement,
  unit,
  onChange,
  onCommit,
  onKeyDown,
  disabled,
  placeholder = "0:00.00",
  className,
}: TimeDurationInputProps) {
  const [text, setText] = useState(() =>
    formatSecondsToTimeString(unitToSeconds(measurement, unit)),
  );
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (!isFocused) {
      setText(formatSecondsToTimeString(unitToSeconds(measurement, unit)));
    }
  }, [measurement, unit, isFocused]);

  const commit = () => {
    const seconds = parseTimeStringToSeconds(text);
    setText(formatSecondsToTimeString(seconds));
    onChange(secondsToUnitValue(seconds, unit), unit);
  };

  return (
    <input
      id={id}
      type="text"
      inputMode="decimal"
      value={text}
      placeholder={placeholder}
      disabled={disabled}
      onFocus={() => setIsFocused(true)}
      onChange={(e) => {
        setText(e.target.value.replace(/[^0-9:.]/g, ""));
      }}
      onBlur={() => {
        setIsFocused(false);
        commit();
        onCommit?.();
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.currentTarget.blur();
        }
        onKeyDown?.(e);
      }}
      title="Enter as seconds (30.30), min:sec (1:08.90), or hr:min:sec (2:30:32)"
      className={className}
    />
  );
}
